import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { 
  ExclamationTriangleIcon,
  MapPinIcon,
  CameraIcon,
  VideoCameraIcon,
  PaperAirplaneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { reportsApi, incidentsApi, locationsApi, checkinsApi } from '../services/api'
import { offlineStorage } from '../services/offlineStorage'
import { checkInStateManager } from '../services/checkInStateManager'
import { useAuth } from '../domains/auth/AuthContext'
import LocationDropdown from '../components/LocationDropdown'

interface IncidentSeverity {
  id: string
  label: string
  level: number
  color: string
}

interface IncidentType {
  id: string
  label: string
  description?: string
  color?: string
  severity: IncidentSeverity
}

interface Location {
  id: string
  name: string
  address: string
}

interface IncidentForm {
  title: string
  description: string
  typeId: string
  locationId: string
  suspectName?: string
  suspectDescription?: string
  witnesses?: string
  pdIncidentNumber?: string
  pdNameBadge?: string
  pdCalledTime?: string
  pdArrivedTime?: string
  supervisorNotified?: string
}

export default function IncidentReport() {
  const navigate = useNavigate()
  const [incidentTypes, setIncidentTypes] = useState<IncidentType[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [currentLocation, setCurrentLocation] = useState<{lat: number, lng: number} | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [currentCheckInLocation, setCurrentCheckInLocation] = useState<Location | null>(null)
  const [selectedType, setSelectedType] = useState<IncidentType | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [selectedVideos, setSelectedVideos] = useState<File[]>([])
  const [isDraftSaving, setIsDraftSaving] = useState(false)
  const [savedFields, setSavedFields] = useState<Set<string>>(new Set())
  
  // Debug wrapper for setSavedFields - DISABLED to prevent infinite loops
  const setSavedFieldsDebug = (updater: React.SetStateAction<Set<string>>) => {
    // DISABLED: Stack trace generation was causing millisecond-level infinite API requests
    // console.log('🔍 setSavedFields called from:', new Error().stack?.split('\n')[2]?.trim())
    setSavedFields(updater) // Direct call without expensive debug logging
  }
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)
  const { user } = useAuth()

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<IncidentForm>()

  useEffect(() => {
    loadFormData()
    getCurrentLocation()
    loadExistingDraft()
    // Prefill location from local snapshot if offline
    prefillLocationFromLocalCheckIn()
  }, [])

  // Prefill location using locally stored current check-in snapshot when offline
  const prefillLocationFromLocalCheckIn = async () => {
    try {
      const isOffline = !navigator.onLine
      if (!isOffline) return
      const snapshot = offlineStorage.getCurrentCheckInSnapshot()
      if (!snapshot) return
      const matched = locations.find(l => l.id === snapshot.locationId)
      const chosen = matched || { 
        id: snapshot.locationId, 
        name: typeof snapshot.location === 'string' ? snapshot.location : (snapshot.location as any)?.name || 'Unknown Location',
        address: typeof snapshot.location === 'string' ? '' : (snapshot.location as any)?.address || ''
      }
      setCurrentCheckInLocation(chosen as any)
      setSelectedLocation(chosen as any)
      setValue('locationId', chosen.id)
    } catch (e) {
      console.warn('Could not prefill location from local snapshot:', e)
    }
  }

  // Load current check-in location after locations are loaded
  const loadCurrentCheckInLocation = async () => {
    try {
      console.log('🔍 IncidentReport - Loading current check-in location...')
      console.log('🔍 IncidentReport - Available locations:', locations.length)
      
      // Prefer online state; if offline, fall back to offline snapshot
      let checkInState = await checkInStateManager.getCurrentCheckInState()
      if (!checkInState && (!navigator.onLine || checkinsApi.getOfflineStatus().isSimulatingOffline)) {
        const snapshot = offlineStorage.getCurrentCheckInSnapshot()
        if (snapshot) {
          checkInState = {
            id: snapshot.id,
            locationId: snapshot.locationId,
            locationName: snapshot.location,
            locationAddress: '',
            checkInTime: snapshot.checkInTime,
            isWithinGeofence: true
          } as any
        }
      }
      console.log('🔍 IncidentReport - Check-in state result:', checkInState)
      
      if (checkInState) {
        console.log('🔍 IncidentReport - Found check-in state:', {
          locationId: checkInState.locationId,
          locationName: checkInState.locationName,
          locationAddress: checkInState.locationAddress
        })
        
        // Find the location in our locations list
        const matchedLocation = locations.find(loc => loc.id === checkInState.locationId)
        console.log('🔍 IncidentReport - Matched location in list:', matchedLocation)
        
        if (matchedLocation) {
          console.log('📍 Auto-assigning check-in location:', matchedLocation.name)
          setCurrentCheckInLocation(matchedLocation)
          setSelectedLocation(matchedLocation)
          setValue('locationId', matchedLocation.id)
        } else {
          // If location not found in list, create a minimal location object
          const checkInLocation = {
            id: checkInState.locationId,
            name: checkInState.locationName,
            address: checkInState.locationAddress,
            organizationId: '',
            centerLat: null,
            centerLng: null,
            radiusMeters: null
          }
          console.log('📍 Auto-assigning check-in location (from state):', checkInLocation.name)
          setCurrentCheckInLocation(checkInLocation)
          setSelectedLocation(checkInLocation)
          setValue('locationId', checkInLocation.id)
        }
      } else {
        console.log('🔍 IncidentReport - No check-in state found')
        // User is not checked in - show error and redirect
        toast.error('You must be checked in to a location to create an incident report')
        // You might want to redirect to check-in page here
        // navigate('/check-in')
      }
    } catch (error) {
      console.error('Error loading current check-in location:', error)
      toast.error('Error loading check-in location. Please check in again.')
    }
  }

  // Effect to load check-in location after locations are loaded
  useEffect(() => {
    if (locations.length > 0) {
      loadCurrentCheckInLocation()
    }
  }, [locations])

  // Monitor savedFields changes
  useEffect(() => {
    console.log('🔍 savedFields changed:', Array.from(savedFields))
  }, [savedFields])

  // Load existing draft on component mount
  const loadExistingDraft = async () => {
    try {
      const organizationId = user?.userOrganizations?.[0]?.organization?.id
      if (!organizationId) return

      let latestDraft: any = null
      
      // First check for offline drafts
      const offlineDraft = offlineStorage.getLocalReportDraft('incident')
      if (offlineDraft) {
        console.log('📱 Found offline incident draft:', offlineDraft)
        latestDraft = {
          id: offlineDraft.localId,
          ...offlineDraft.data,
          offline: true
        }
      }
      
      // Then check server drafts if online
      if (navigator.onLine) {
        try {
          const draftsResponse = await reportsApi.getDraftIncidentReports()
          const serverDrafts = draftsResponse?.data || []
          
          console.log('📋 Found server incident drafts:', serverDrafts.length)
          
          if (serverDrafts.length > 0) {
            const serverDraft = serverDrafts[0] // Most recent draft
            
            // If we have both offline and server drafts, merge them intelligently
            if (offlineDraft && serverDraft) {
              console.log('🔄 Merging offline and server drafts')
              latestDraft = await offlineStorage.resolveReportConflicts(offlineDraft, serverDraft)
              latestDraft.id = serverDraft.id
            } else if (serverDraft) {
              latestDraft = serverDraft
            }
          }
        } catch (error) {
          console.log('Server draft loading failed, using offline draft only:', error)
        }
      }
      
      if (latestDraft) {
        console.log('📋 Loading existing draft data:', latestDraft)
        setCurrentDraftId(latestDraft.id)
        
        // Only populate fields that have actual content (not default values)
        if (latestDraft.title && latestDraft.title !== 'Draft Incident Report') {
          setValue('title', latestDraft.title)
        }
        if (latestDraft.description && latestDraft.description !== 'Draft in progress...') {
          setValue('description', latestDraft.description)
        }
        
        if (latestDraft.incidentTypeId) setValue('typeId', latestDraft.incidentTypeId)
        if (latestDraft.locationId) setValue('locationId', latestDraft.locationId)
        if (latestDraft.suspectName) setValue('suspectName', latestDraft.suspectName)
        if (latestDraft.suspectDescription) setValue('suspectDescription', latestDraft.suspectDescription)
        if (latestDraft.witnesses) setValue('witnesses', latestDraft.witnesses)
        if (latestDraft.pdIncidentNumber) setValue('pdIncidentNumber', latestDraft.pdIncidentNumber)
        if (latestDraft.pdNameBadge) setValue('pdNameBadge', latestDraft.pdNameBadge)
        if (latestDraft.pdCalledTime) setValue('pdCalledTime', latestDraft.pdCalledTime)
        if (latestDraft.pdArrivedTime) setValue('pdArrivedTime', latestDraft.pdArrivedTime)
        if (latestDraft.supervisorNotified) setValue('supervisorNotified', latestDraft.supervisorNotified)

        // Set selected location and type
        if (latestDraft.locationId) {
          const location = locations.find(l => l.id === latestDraft.locationId)
          if (location) {
            setSelectedLocation(location)
            setValue('locationId', location.id) // Ensure form value is also set
          }
        }
        
        if (latestDraft.incidentTypeId) {
          const type = incidentTypes.find(t => t.id === latestDraft.incidentTypeId)
          if (type) {
            setSelectedType(type)
            setValue('typeId', type.id) // Ensure form value is also set
          }
        }

        // Show offline indicator if applicable
        if (latestDraft.offline) {
          toast.success('Loaded offline draft - will sync when connection is restored')
        }
      } else {
        console.log('📋 No existing draft found - user can start fresh')
      }
    } catch (error) {
      console.error('Error loading draft:', error)
    }
  }

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
      },
      (error) => {
        console.error('Error getting location:', error)
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    )
  }

  const cleanupDuplicateDrafts = async () => {
    try {
      if (navigator.onLine) {
        const draftsResponse = await reportsApi.getDraftIncidentReports()
        const drafts = draftsResponse?.data || []
        
        if (drafts.length > 1) {
          console.log(`🧹 Found ${drafts.length} drafts, keeping only the most recent one`)
          
          // Keep the most recent draft (first in the array) and delete the rest
          const draftsToDelete = drafts.slice(1)
          
          for (const draft of draftsToDelete) {
            try {
              await reportsApi.deleteIncidentReportDraft(draft.id)
              console.log(`🗑️ Deleted duplicate draft: ${draft.id}`)
            } catch (error) {
              console.error(`Failed to delete draft ${draft.id}:`, error)
            }
          }
          
          console.log(`✅ Cleaned up ${draftsToDelete.length} duplicate drafts`)
        }
      }
    } catch (error) {
      console.error('Error cleaning up duplicate drafts:', error)
    }
  }

  const loadFormData = async () => {
    try {
      setLoading(true)
      
      // Get organization ID from user's first organization
      const organizationId = user?.userOrganizations?.[0]?.organization?.id
      
      if (!organizationId) {
        toast.error('No organization found. Please contact your administrator.')
        return
      }

      console.log('🔍 Loading incident types for organization:', organizationId)
      
      try {
        // Fetch real data from API
        const [typesResponse, locationsResponse] = await Promise.all([
          incidentsApi.getIncidentTypes(organizationId),
          locationsApi.getLocations()
        ])

        console.log('🔍 Types response:', typesResponse)
        console.log('🔍 Locations response:', locationsResponse)

        // Handle the API response format
        const types = typesResponse?.data || typesResponse || []
        const locations = locationsResponse?.data || locationsResponse || []

        if (types.length === 0) {
          toast.error('No incident types found for your organization. Please contact your administrator.')
          return
        }

        setIncidentTypes(types)
        setLocations(locations)
        
        // Clean up any duplicate drafts after loading form data
        await cleanupDuplicateDrafts()
      } catch (apiError) {
        console.error('API error:', apiError)
        toast.error('Failed to load incident types. Please check your connection and try again.')
        setIncidentTypes([])
        setLocations([])
      }
    } catch (error) {
      console.error('Error loading form data:', error)
      toast.error('Failed to load form data')
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length > 0) {
      setSelectedImages(prev => [...prev, ...files])
      
      // Store files offline for later sync if needed
      const isOnline = navigator.onLine
      if (!isOnline) {
        for (const file of files) {
          await offlineStorage.storeOfflineMediaFile(file, 'image')
        }
        toast.success(`${files.length} photo(s) added and stored offline`)
      } else {
        toast.success(`${files.length} photo(s) added`)
      }
    }
  }

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length > 0) {
      setSelectedVideos(prev => [...prev, ...files])
      
      // Store files offline for later sync if needed
      const isOnline = navigator.onLine
      if (!isOnline) {
        for (const file of files) {
          await offlineStorage.storeOfflineMediaFile(file, 'video')
        }
        toast.success(`${files.length} video(s) added and stored offline`)
      } else {
        toast.success(`${files.length} video(s) added`)
      }
    }
  }

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index))
  }

  const removeVideo = (index: number) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index))
  }

  const onSubmit = async (data: IncidentForm) => {
    try {
      setSubmitting(true)
      
      // Check if user is checked in
      if (!currentCheckInLocation) {
        toast.error('You must be checked in to a location to create an incident report')
        setSubmitting(false)
        return
      }
      
      // Validate required fields
      if (!data.title || !data.description || !data.typeId || !data.locationId) {
        toast.error('Please fill in all required fields (Title, Description, Incident Type, and Location)')
        return
      }

      // Get current location if available
      let latitude: number | undefined
      let longitude: number | undefined
      
      if (navigator.geolocation) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          })
        })
        
        latitude = position.coords.latitude
        longitude = position.coords.longitude
      }

      const organizationId = user?.userOrganizations?.[0]?.organization?.id
      
      if (!organizationId) {
        toast.error('No organization found. Please contact your administrator.')
        return
      }

      // Get the selected incident type to determine severity
      const selectedIncidentType = incidentTypes.find(type => type.id === data.typeId)
      if (!selectedIncidentType) {
        toast.error('Please select a valid incident type.')
        return
      }

      console.log('Submitting incident report with data:', {
        ...data,
        organizationId,
        incidentTypeId: data.typeId,
        severityId: selectedIncidentType.severity.id,
        latitude,
        longitude
      })
      
      // Clean up empty datetime fields
      const cleanedData = { ...data }
      if (cleanedData.pdCalledTime === '') {
        cleanedData.pdCalledTime = undefined
        console.log('🧹 Cleaned pdCalledTime from empty string to undefined')
      }
      if (cleanedData.pdArrivedTime === '') {
        cleanedData.pdArrivedTime = undefined
        console.log('🧹 Cleaned pdArrivedTime from empty string to undefined')
      }

      const incidentData = {
        organizationId,
        locationId: cleanedData.locationId,
        incidentTypeId: cleanedData.typeId,
        severityId: selectedIncidentType.severity.id,
        title: cleanedData.title,
        description: cleanedData.description,
        suspectName: cleanedData.suspectName,
        suspectDescription: cleanedData.suspectDescription,
        witnesses: cleanedData.witnesses,
        pdIncidentNumber: cleanedData.pdIncidentNumber,
        pdNameBadge: cleanedData.pdNameBadge,
        pdCalledTime: cleanedData.pdCalledTime ? (() => {
          console.log('🔍 onSubmit - pdCalledTime raw:', cleanedData.pdCalledTime)
          const converted = new Date(cleanedData.pdCalledTime).toISOString()
          console.log('✅ onSubmit - pdCalledTime converted:', converted)
          return converted
        })() : undefined,
        pdArrivedTime: cleanedData.pdArrivedTime ? (() => {
          console.log('🔍 onSubmit - pdArrivedTime raw:', cleanedData.pdArrivedTime)
          const converted = new Date(cleanedData.pdArrivedTime).toISOString()
          console.log('✅ onSubmit - pdArrivedTime converted:', converted)
          return converted
        })() : undefined,
        supervisorNotified: cleanedData.supervisorNotified,
        latitude,
        longitude,
        isDraft: false // Submit as final report
      }

      let reportId: string

      if (currentDraftId) {
        // Publish existing draft
        await reportsApi.publishIncidentReport(currentDraftId)
        reportId = currentDraftId
        toast.success('Incident report submitted successfully')
      } else {
        // Create new report using reports API (consistent with draft creation)
        const response = await reportsApi.createIncidentReportViaReports(incidentData)
        reportId = response.id || response.data?.id
        toast.success('Incident report submitted successfully')
      }

      // Upload images and videos if they exist
      if (reportId && (selectedImages.length > 0 || selectedVideos.length > 0)) {
        console.log('Uploading media files...')
        
        // Upload images
        for (const image of selectedImages) {
          try {
            const formData = new FormData()
            formData.append('image', image)
            
            await fetch((import.meta.env.VITE_API_BASE_URL || 'http://localhost:17000/api/app').replace('/api/app', '') + '/api/app/reports/incidents/' + reportId + '/images', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              },
              body: formData
            })
          } catch (error) {
            console.error('Error uploading image:', error)
            toast.error('Failed to upload some images')
          }
        }

        // Upload videos
        for (const video of selectedVideos) {
          try {
            const formData = new FormData()
            formData.append('video', video)
            
            await fetch((import.meta.env.VITE_API_BASE_URL || 'http://localhost:17000/api/app').replace('/api/app', '') + '/api/app/reports/incidents/' + reportId + '/videos', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              },
              body: formData
            })
          } catch (error) {
            console.error('Error uploading video:', error)
            toast.error('Failed to upload some videos')
          }
        }

        if (selectedImages.length > 0 || selectedVideos.length > 0) {
          toast.success('Media files uploaded successfully')
        }
      }
      
      // Clear form and draft state
      reset()
      setSelectedImages([])
      setSelectedVideos([])
      setSelectedLocation(null)
      setSelectedType(null)
      setCurrentDraftId(null)
      setSavedFieldsDebug(new Set())
      
      // Navigate back to reports page
      navigate('/reports')
    } catch (error: any) {
      console.error('Error submitting incident report:', error)
      toast.error(error.response?.data?.error || 'Failed to submit incident report')
    } finally {
      setSubmitting(false)
    }
  }

  // Auto-save draft when field loses focus
  const autoSaveDraft = async (fieldName: string) => {
    let draftData: any = null // Declare outside try block for error logging
    
    try {
      setIsDraftSaving(true)
      const formData = getValues()
      
      // Clean up empty datetime fields
      const cleanedFormData = { ...formData }
      if (cleanedFormData.pdCalledTime === '') {
        cleanedFormData.pdCalledTime = undefined
      }
      if (cleanedFormData.pdArrivedTime === '') {
        cleanedFormData.pdArrivedTime = undefined
      }
      
      // Get the current field value to check if there's something to save
      const currentFieldValue = cleanedFormData[fieldName as keyof IncidentForm]
      console.log(`🔍 Checking field ${fieldName} with value:`, currentFieldValue)
      
      // Special handling for datetime fields - they can be empty strings
      if (fieldName === 'pdCalledTime' || fieldName === 'pdArrivedTime') {
        if (!currentFieldValue || currentFieldValue.toString().trim() === '') {
          console.log(`⚠️ No content to save for datetime field: ${fieldName}`)
          return
        }
        // For datetime fields, also check if it's a valid date
        try {
          const date = new Date(currentFieldValue.toString())
          if (isNaN(date.getTime())) {
            console.log(`⚠️ Invalid date for field: ${fieldName}`)
            return
          }
        } catch (error) {
          console.log(`⚠️ Error validating date for field: ${fieldName}`)
          return
        }
      } else {
        // For non-datetime fields, only save if they have content
        if (!currentFieldValue || currentFieldValue.toString().trim() === '') {
          console.log(`⚠️ No content to save for field: ${fieldName}`)
          return
        }
      }
      
      console.log(`✅ Field ${fieldName} has content, proceeding with save...`)

      const organizationId = user?.userOrganizations?.[0]?.organization?.id
      
      if (!organizationId) {
        console.log('No organization ID found')
        return
      }

      // For draft, we can have optional incident type and severity
      const selectedIncidentType = incidentTypes.find(type => type.id === cleanedFormData.typeId)
      
      // Clean up the form data - only send fields that have values
      draftData = {
        organizationId,
        isDraft: true
      }

      // Only add fields that have actual values
      if (cleanedFormData.locationId) draftData.locationId = cleanedFormData.locationId
      if (cleanedFormData.typeId) draftData.incidentTypeId = cleanedFormData.typeId
      if (selectedIncidentType?.severity?.id) draftData.severityId = selectedIncidentType.severity.id
      if (cleanedFormData.title?.trim()) draftData.title = cleanedFormData.title.trim()
      if (cleanedFormData.description?.trim()) draftData.description = cleanedFormData.description.trim()
      if (cleanedFormData.suspectName?.trim()) draftData.suspectName = cleanedFormData.suspectName.trim()
      if (cleanedFormData.suspectDescription?.trim()) draftData.suspectDescription = cleanedFormData.suspectDescription.trim()
      if (cleanedFormData.witnesses?.trim()) draftData.witnesses = cleanedFormData.witnesses.trim()
      if (cleanedFormData.pdIncidentNumber?.trim()) draftData.pdIncidentNumber = cleanedFormData.pdIncidentNumber.trim()
      if (cleanedFormData.pdNameBadge?.trim()) draftData.pdNameBadge = cleanedFormData.pdNameBadge.trim()
      if (cleanedFormData.supervisorNotified?.trim()) draftData.supervisorNotified = cleanedFormData.supervisorNotified.trim()

      // Convert datetime-local format to ISO-8601 for API
      if (cleanedFormData.pdCalledTime && cleanedFormData.pdCalledTime.trim() !== '') {
        try {
          console.log('🔍 Processing pdCalledTime:', cleanedFormData.pdCalledTime)
          const date = new Date(cleanedFormData.pdCalledTime)
          if (!isNaN(date.getTime())) {
            draftData.pdCalledTime = date.toISOString()
            console.log('✅ Converted pdCalledTime to:', draftData.pdCalledTime)
          } else {
            console.warn('⚠️ Invalid pdCalledTime date:', cleanedFormData.pdCalledTime)
          }
        } catch (error) {
          console.error('Error converting pdCalledTime:', error)
        }
      } else {
        console.log('🔍 pdCalledTime is empty or null')
      }
      
      if (cleanedFormData.pdArrivedTime && cleanedFormData.pdArrivedTime.trim() !== '') {
        try {
          console.log('🔍 Processing pdArrivedTime:', cleanedFormData.pdArrivedTime)
          const date = new Date(cleanedFormData.pdArrivedTime)
          if (!isNaN(date.getTime())) {
            draftData.pdArrivedTime = date.toISOString()
            console.log('✅ Converted pdArrivedTime to:', draftData.pdArrivedTime)
          } else {
            console.warn('⚠️ Invalid pdArrivedTime date:', cleanedFormData.pdArrivedTime)
          }
        } catch (error) {
          console.error('Error converting pdArrivedTime:', error)
        }
      } else {
        console.log('🔍 pdArrivedTime is empty or null')
      }

      // Always provide defaults for required fields if they're missing
      if (!draftData.title) draftData.title = 'Draft Incident Report'
      if (!draftData.description) draftData.description = 'Draft in progress...'

      console.log(`Auto-saving field: ${fieldName}`, draftData)

      // Check if we're online
      const isOnline = navigator.onLine
      let response: any

      if (isOnline) {
        try {
          if (currentDraftId) {
            // Update existing draft
            response = await reportsApi.updateIncidentReportDraft(currentDraftId, draftData)
            console.log('Updated existing draft')
          } else {
            // Check if there's an existing draft before creating a new one
            try {
              const draftsResponse = await reportsApi.getDraftIncidentReports()
              const existingDrafts = draftsResponse?.data || []
              
              if (existingDrafts.length > 0) {
                // Use the existing draft instead of creating a new one
                const existingDraft = existingDrafts[0]
                setCurrentDraftId(existingDraft.id)
                response = await reportsApi.updateIncidentReportDraft(existingDraft.id, draftData)
                console.log('Updated existing draft instead of creating new one')
              } else {
                // Create new draft only if no existing drafts
                response = await reportsApi.createIncidentReportViaReports(draftData)
                setCurrentDraftId(response.id || response.localId)
                console.log('Created new draft with ID:', response.id || response.localId)
              }
            } catch (draftCheckError) {
              console.log('Failed to check existing drafts, creating new one:', draftCheckError)
              response = await reportsApi.createIncidentReportViaReports(draftData)
              setCurrentDraftId(response.id || response.localId)
              console.log('Created new draft with ID:', response.id || response.localId)
            }
          }
        } catch (error) {
          // If API call fails, fall back to offline storage
          console.log('API call failed, falling back to offline storage')
          throw error
        }
      } else {
        // We're offline, use offline storage
        console.log('📱 Offline mode: storing draft locally')
        
        // Include media files in offline draft
        const mediaFiles: any[] = []
        
        // Add selected images
        for (const imageFile of selectedImages) {
          const mediaId = await offlineStorage.storeOfflineMediaFile(imageFile, 'image')
          mediaFiles.push({
            id: mediaId,
            type: 'image',
            file: imageFile,
            synced: false
          })
        }
        
        // Add selected videos  
        for (const videoFile of selectedVideos) {
          const mediaId = await offlineStorage.storeOfflineMediaFile(videoFile, 'video')
          mediaFiles.push({
            id: mediaId,
            type: 'video',
            file: videoFile,
            synced: false
          })
        }
        
        if (currentDraftId) {
          // Update existing draft offline
          const localId = await offlineStorage.storeOfflineReportDraft({
            type: 'incident',
            reportId: currentDraftId, // Keep reference to server ID
            data: draftData,
            mediaFiles
          })
          setCurrentDraftId(localId)
          response = { offline: true, localId }
          console.log('Updated existing draft offline')
        } else {
          // Check for existing offline draft before creating new one
          const existingOfflineDraft = offlineStorage.getLocalReportDraft('incident')
          if (existingOfflineDraft) {
            setCurrentDraftId(existingOfflineDraft.localId)
            const localId = await offlineStorage.storeOfflineReportDraft({
              type: 'incident',
              reportId: existingOfflineDraft.reportId,
              data: draftData,
              mediaFiles
            })
            response = { offline: true, localId }
            console.log('Updated existing offline draft')
          } else {
            // Create new offline draft
            const localId = await offlineStorage.storeOfflineReportDraft({
              type: 'incident',
              data: draftData,
              mediaFiles
            })
            setCurrentDraftId(localId)
            response = { offline: true, localId }
            console.log('Created new offline draft')
          }
        }
        console.log('📱 Stored offline draft with media files:', response)
      }

      // Show appropriate message
      if (response?.offline) {
        toast.success(`${fieldName} saved offline - will sync when connection is restored`)
      }

      // Mark field as saved
      console.log(`✅ Setting checkmark for field: ${fieldName}`)
      setSavedFieldsDebug(prev => {
        const newSet = new Set([...prev, fieldName])
        console.log(`✅ Updated savedFields:`, Array.from(newSet))
        return newSet
      })
      
      // Temporarily disable automatic clearing to debug the location selection issue
      // Clear saved indicator after 3 seconds
      // setTimeout(() => {
      //   console.log(`⏰ Removing checkmark for field: ${fieldName}`)
      //   setSavedFieldsDebug(prev => {
      //     const newSet = new Set(prev)
      //     newSet.delete(fieldName)
      //     console.log(`⏰ Updated savedFields after timeout:`, Array.from(newSet))
      //     return newSet
      //   })
      // }, 3000)

    } catch (error: any) {
      console.error('❌ Error auto-saving draft:', error)
      console.error('❌ Error details:', error.response?.data || error.message)
      console.error('❌ Field being saved:', fieldName)
      console.error('❌ Data being sent:', draftData)
      
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
      toast.error(`Failed to save ${fieldName}: ${errorMessage}`)
    } finally {
      setIsDraftSaving(false)
    }
  }

  // Clear ALL drafts for this user
  const clearDraft = async () => {
    try {
      console.log('🗑️ Clearing all drafts...')
      let totalDeleted = 0

      // Clear offline incident reports storage first
      const offlineDrafts = offlineStorage.getOfflineReportDrafts()
      const incidentDrafts = offlineDrafts.filter(d => d.type === 'incident')
      
      // Clear each offline draft
      for (const draft of incidentDrafts) {
        offlineStorage.clearReportDraft(draft.localId)
        totalDeleted++
      }
      
      // Try to delete server drafts if online
      try {
        const result = await reportsApi.deleteAllIncidentReportDrafts()
        console.log('🗑️ Server delete result:', result)
        totalDeleted += result.count || 0
      } catch (error) {
        // If offline or network error, just log it - we've already cleared offline drafts
        const err = error as { message?: string; code?: string }
        if (err.message?.includes('Network Error') || err.code === 'NETWORK_ERROR') {
          console.log('📱 Offline/Network error while clearing server drafts - skipping')
        } else {
          throw error // Re-throw unexpected errors
        }
      }
      
      // Reset form
      reset()
      setSelectedLocation(null)
      setSelectedType(null)
      setSelectedImages([])
      setSelectedVideos([])
      setCurrentDraftId(null)
      setSavedFieldsDebug(new Set())
      
      toast.success(`Report cleared (${totalDeleted} drafts deleted)`)
    } catch (error) {
      console.error('❌ Error clearing draft:', error)
      toast.error('Failed to clear report')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
      </div>
    )
  }

  return (
    <div className="p-4 pb-40 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <ExclamationTriangleIcon className="h-6 w-6 text-error" />
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-tech-50">Incident Report</h1>
          <p className="text-sm text-gray-600 dark:text-tech-400">Report an incident or emergency</p>
          {currentDraftId && (
            <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
              📄 Draft loaded - Auto-saving enabled
            </p>
          )}
          {isDraftSaving && (
            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
              💾 Saving draft...
            </p>
          )}
        </div>
      </div>



      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className={`space-y-6 ${!currentCheckInLocation ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Incident Title *
          </label>
          <div className="relative">
            <input
              type="text"
              {...register('title', { required: 'Title is required' })}
              onBlur={() => autoSaveDraft('title')}
              className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              placeholder="Brief description of the incident"
            />
            {savedFields.has('title') && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className="text-green-500 text-sm">✓</span>
              </div>
            )}
          </div>
          {errors.title && (
            <p className="text-error dark:text-red-400 text-sm mt-1">{errors.title.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Description *
          </label>
          <div className="relative">
            <textarea
              {...register('description', { required: 'Description is required' })}
              onBlur={() => autoSaveDraft('description')}
              rows={4}
              className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              placeholder="Provide detailed information about the incident..."
            />
            {savedFields.has('description') && (
              <div className="absolute right-3 top-3">
                <span className="text-green-500 text-sm">✓</span>
              </div>
            )}
          </div>
          {errors.description && (
            <p className="text-error dark:text-red-400 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Incident Type */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Incident Type *
          </label>
          <div className="relative">
            <select
              {...register('typeId', { required: 'Incident type is required' })}
              onChange={(e) => {
                const selectedType = incidentTypes.find(type => type.id === e.target.value)
                setSelectedType(selectedType || null)
                // Update form value and trigger auto-save
                setValue('typeId', e.target.value)
                // Use setTimeout to ensure the form value is updated before auto-save
                setTimeout(() => {
                  autoSaveDraft('typeId')
                }, 0)
              }}
              className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
            >
              <option value="">Select incident type</option>
              {incidentTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label} - {type.severity.label} ({type.severity.level})
                </option>
              ))}
            </select>
            {savedFields.has('typeId') && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className="text-green-500 text-sm">✓</span>
              </div>
            )}
          </div>
          {errors.typeId && (
            <p className="text-error dark:text-red-400 text-sm mt-1">{errors.typeId.message}</p>
          )}
        </div>

        {/* Severity Display */}
        {selectedType && (
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Severity Level (Assigned)
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg bg-gray-50 dark:bg-tech-800 flex items-center space-x-3">
              <div 
                className="w-4 h-4 rounded-full border border-gray-300 dark:border-tech-600"
                style={{ backgroundColor: selectedType.severity.color }}
              />
              <span className="text-gray-900 dark:text-tech-100 font-medium">
                {selectedType.severity.label} (Level {selectedType.severity.level})
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-tech-400 mt-1">
              Severity is automatically assigned based on the incident type.
            </p>
          </div>
        )}

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Location *
          </label>
          {currentCheckInLocation ? (
            // Show locked location when user is checked in
            <div className="relative">
              <div className="w-full px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <span className="text-gray-900 dark:text-tech-100 font-medium">
                    {typeof currentCheckInLocation.name === 'string' ? currentCheckInLocation.name : 'Unknown Location'}
                  </span>
                  <p className="text-xs text-gray-600 dark:text-tech-400 mt-1">
                    {typeof currentCheckInLocation.address === 'string' ? currentCheckInLocation.address : ''}
                  </p>
                </div>
                <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                  Current Check-in Location
                </div>
              </div>
              {savedFields.has('locationId') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          ) : (
            // Show error message when user is not checked in
            <div className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <div className="flex-1">
                <span className="text-red-900 dark:text-red-100 font-medium">
                  No Check-in Location
                </span>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  You must be checked in to create an incident report
                </p>
              </div>
            </div>
          )}
          {!currentCheckInLocation && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              ❌ You must be checked in to a location to create an incident report. Please go to the Check-in page first.
            </p>
          )}
          {errors.locationId && (
            <p className="text-error dark:text-red-400 text-sm mt-1">{errors.locationId.message}</p>
          )}
        </div>

        {/* Suspect Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Suspect Name
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('suspectName')}
                onBlur={() => autoSaveDraft('suspectName')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                placeholder="Name of suspect (if known)"
              />
              {savedFields.has('suspectName') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              P.D. Incident Report #
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('pdIncidentNumber')}
                onBlur={() => autoSaveDraft('pdIncidentNumber')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                placeholder="Police incident number"
              />
              {savedFields.has('pdIncidentNumber') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Witnesses */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Name of Witnesses
          </label>
          <div className="relative">
            <textarea
              {...register('witnesses')}
              onBlur={() => autoSaveDraft('witnesses')}
              rows={2}
              className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              placeholder="List any witnesses to the incident..."
            />
            {savedFields.has('witnesses') && (
              <div className="absolute right-3 top-3">
                <span className="text-green-500 text-sm">✓</span>
              </div>
            )}
          </div>
        </div>

        {/* Police Department Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              P.D. Name & Badge #
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('pdNameBadge')}
                onBlur={() => autoSaveDraft('pdNameBadge')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                placeholder="Officer name and badge number"
              />
              {savedFields.has('pdNameBadge') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Supervisor Notified
            </label>
            <div className="relative">
              <input
                type="text"
                {...register('supervisorNotified')}
                onBlur={() => autoSaveDraft('supervisorNotified')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
                placeholder="Name of supervisor notified"
              />
              {savedFields.has('supervisorNotified') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Police Department Times */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Time P.D. Called
            </label>
            <div className="relative">
              <input
                type="datetime-local"
                {...register('pdCalledTime')}
                onBlur={() => autoSaveDraft('pdCalledTime')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              />
              {savedFields.has('pdCalledTime') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
            {errors.pdCalledTime && (
              <p className="text-error dark:text-red-400 text-sm mt-1">{errors.pdCalledTime.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Time P.D. Arrived
            </label>
            <div className="relative">
              <input
                type="datetime-local"
                {...register('pdArrivedTime')}
                onBlur={() => autoSaveDraft('pdArrivedTime')}
                className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              />
              {savedFields.has('pdArrivedTime') && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-green-500 text-sm">✓</span>
                </div>
              )}
            </div>
            {errors.pdArrivedTime && (
              <p className="text-error dark:text-red-400 text-sm mt-1">{errors.pdArrivedTime.message}</p>
            )}
          </div>
        </div>

        {/* Suspect Description */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
            Suspect Description
          </label>
          <div className="relative">
            <textarea
              {...register('suspectDescription')}
              onBlur={() => autoSaveDraft('suspectDescription')}
              rows={3}
              className="w-full px-3 py-2 bg-white dark:bg-tech-800 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent text-gray-900 dark:text-tech-100"
              placeholder="Detailed description of the suspect (appearance, clothing, etc.)..."
            />
            {savedFields.has('suspectDescription') && (
              <div className="absolute right-3 top-3">
                <span className="text-green-500 text-sm">✓</span>
              </div>
            )}
          </div>
        </div>

        {/* Media Upload */}
        <div className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Photos (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-tech-700 rounded-lg p-6 text-center bg-gray-50 dark:bg-tech-800 hover:border-primary-orange transition-colors">
              <CameraIcon className="h-12 w-12 text-gray-400 dark:text-tech-500 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-tech-400 mb-3">
                Tap to add photos of the incident
              </p>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="inline-flex items-center px-4 py-2 bg-primary-orange text-white text-sm font-medium rounded-lg hover:bg-primary-orange-dark transition-colors cursor-pointer"
              >
                <CameraIcon className="h-4 w-4 mr-2" />
                Add Photos
              </label>
            </div>
            
            {/* Selected Images */}
            {selectedImages.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-900 dark:text-tech-50">Selected Images:</p>
                {selectedImages.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-tech-800 rounded">
                    <span className="text-sm text-gray-600 dark:text-tech-400">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="text-error dark:text-red-400 hover:text-error-dark dark:hover:text-red-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-tech-50 mb-2">
              Videos (Optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-tech-700 rounded-lg p-6 text-center bg-gray-50 dark:bg-tech-800 hover:border-primary-orange transition-colors">
              <VideoCameraIcon className="h-12 w-12 text-gray-400 dark:text-tech-500 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-tech-400 mb-3">
                Tap to add videos of the incident
              </p>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleVideoUpload}
                className="hidden"
                id="video-upload"
              />
              <label
                htmlFor="video-upload"
                className="inline-flex items-center px-4 py-2 bg-primary-orange text-white text-sm font-medium rounded-lg hover:bg-primary-orange-dark transition-colors cursor-pointer"
              >
                <VideoCameraIcon className="h-4 w-4 mr-2" />
                Add Videos
              </label>
            </div>
            
            {/* Selected Videos */}
            {selectedVideos.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-900 dark:text-tech-50">Selected Videos:</p>
                {selectedVideos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-tech-800 rounded">
                    <span className="text-sm text-gray-600 dark:text-tech-400">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeVideo(index)}
                      className="text-error dark:text-red-400 hover:text-error-dark dark:hover:text-red-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mb-8">
          {/* Clear Report Button - Always visible */}
          <button
            type="button"
            onClick={clearDraft}
            className="flex-1 bg-red-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-600 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!currentDraftId && savedFields.size === 0}
          >
            <span>🗑️</span>
            <span>Clear Report</span>
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || submitting}
            className="flex-1 bg-primary-orange text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-orange-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="h-4 w-4" />
                <span>Submit Report</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Emergency Notice */}
      <div className="bg-error/10 dark:bg-red-900/20 border border-error/20 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-error dark:text-red-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-error dark:text-red-400">Emergency Notice</h3>
            <p className="text-xs text-gray-600 dark:text-tech-400 mt-1">
              If this is a life-threatening emergency, call emergency services immediately.
              This report is for documentation purposes only.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 